import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  PinIcon,
  DeleteIcon,
  ShareIcon,
  LockIcon,
  MoveIcon,
  ReportIcon,
  EditIcon,
  SubscribeIcon,
} from '../../components/icons';
import ActionButton from '../../components/action-button';
import { pinPost } from '../../actions/pin-post';
import { unpinPost } from '../../actions/unpin-post';
import { enablePostComments } from '../../actions/enable-post-comments';
import { disablePostComments } from '../../actions/disable-post-comments';
import { openModal } from '../../actions/open-modal';
import { subscribe } from '../../actions/subscribe';
import { unsubscribe } from '../../actions/unsubscribe';
import { buttonClicked } from '../../actions/button-clicked';
import { actionsOpened } from '../../actions/actions-opened';
import { actionsClosed } from '../../actions/actions-closed';
import {
  MODAL_TYPE_SHARE_POST,
  MODAL_TYPE_DELETE_POST,
  MODAL_TYPE_MOVE_POST,
  MODAL_TYPE_REPORT_POST,
} from '../../constants/modal-types';
import messages, {
  POST_PIN,
  POST_UNPIN,
  POST_ENABLE_COMMENTING,
  POST_DISABLE_COMMENTING,
  POST_SUBSCRIBED,
  POST_UNSUBSCRIBED,
} from '../../components/message/messages';
import { showShare } from '../../services/show-share';
import withPermissions from '../../hoc/with-permissions';
import withTranslate from '../../hoc/with-translate';
import { getCategory, getCategoryCount } from '../../selectors/categories-selectors';
import withEnvironment from '../../hoc/with-environment';
import withAuth from '../../hoc/with-auth';

export class PostActions extends Component {
  constructor(props) {
    super(props);

    this.handlePin = this.handlePin.bind(this);
    this.handleComments = this.handleComments.bind(this);
    this.showModal = this.showModal.bind(this);
    this.handleSubscription = this.handleSubscription.bind(this);
  }

  componentDidMount() {
    this.props.actionsOpened({ type: 'post' });
  }

  componentWillUnmount() {
    this.props.actionsClosed({ type: 'post' });
  }

  handlePin() {
    const { post: { _id, isPinned }, buttonClicked, pinPost, unpinPost, showMessage } = this.props;

    buttonClicked({ action: 'pin', flag: Number(!isPinned) });
    if (isPinned) {
      unpinPost(_id).then(() => showMessage(messages[POST_UNPIN].content(), messages[POST_UNPIN].type));
    } else {
      pinPost(_id).then(() => showMessage(messages[POST_PIN].content(), messages[POST_PIN].type));
    }
  }

  handleSubscription() {
    const { post: { _id, isSubscribed }, buttonClicked, subscribe, unsubscribe, showMessage } = this.props;

    buttonClicked({ action: 'follow_post', flag: Number(!isSubscribed) });
    if (isSubscribed) {
      unsubscribe({ _id, type: 'post' }).then(() =>
        showMessage(messages[POST_UNSUBSCRIBED].content(), messages[POST_UNSUBSCRIBED].type)
      );
    } else {
      subscribe({ _id, type: 'post' }).then(() =>
        showMessage(messages[POST_SUBSCRIBED].content(), messages[POST_SUBSCRIBED].type)
      );
    }
  }

  handleComments() {
    const {
      post: { _id, isCommentsDisabled },
      buttonClicked,
      enablePostComments,
      disablePostComments,
      showMessage,
    } = this.props;

    buttonClicked({ action: 'lock', flag: Number(!isCommentsDisabled) });
    if (isCommentsDisabled) {
      enablePostComments(_id).then(() =>
        showMessage(messages[POST_ENABLE_COMMENTING].content(), messages[POST_ENABLE_COMMENTING].type)
      );
    } else {
      disablePostComments(_id).then(() =>
        showMessage(messages[POST_DISABLE_COMMENTING].content(), messages[POST_DISABLE_COMMENTING].type)
      );
    }
  }

  showModal(type) {
    const props = {
      categoryId: this.props.category._id,
      categorySlug: this.props.category.slug,
      postId: this.props.post._id,
      postSlug: this.props.post.slug,
    };
    const { buttonClicked, openModal, focusOnCloseId } = this.props;

    return () => {
      buttonClicked({ action: type });
      openModal(type, props, focusOnCloseId);
    };
  }

  navigateToEditPost = () => {
    const { buttonClicked, category, navigation: { navigateToEditPost }, post } = this.props;
    navigateToEditPost(`/${category.slug}/${post.slug}/edit`);
    buttonClicked({ action: 'edit' });
  };

  render() {
    const { canRender, post, categoryCount, t, forPublicUser } = this.props;
    const editButton = canRender('edit', 'post', post, can => (
      <ActionButton
        data-hook="post-actions__edit"
        onClick={can ? this.navigateToEditPost : forPublicUser(this.navigateToEditPost)}
      >
        <EditIcon className="forum-icon-fill"/>
        {t('post-actions.edit-post')}
      </ActionButton>
    ));
    const shareButton = showShare() && canRender('share', 'post', post, () => (
      <ActionButton data-hook="post-actions__share" onClick={this.showModal(MODAL_TYPE_SHARE_POST)}>
        <ShareIcon className="forum-icon-fill"/>
        {t('post-actions.share-post')}
      </ActionButton>
    ));
    const pinButton = canRender('pin', 'post', post, () => (
      <ActionButton data-hook="post-actions__pin" onClick={this.handlePin}>
        <PinIcon isPinned={post.isPinned} className="forum-icon-fill"/>
        {post.isPinned ? t('post-actions.unpin-post') : t('post-actions.pin-post')}
      </ActionButton>
    ));
    const commentsButton = canRender('toggle-comments', 'post', post, () => (
      <ActionButton data-hook="post-actions__comments" onClick={this.handleComments}>
        <LockIcon isLocked={post.isCommentsDisabled} className="forum-icon-fill"/>
        {post.isCommentsDisabled ? t('post-actions.enable-commenting') : t('post-actions.disable-commenting')}
      </ActionButton>
    ));
    const moveButton = categoryCount > 1 && canRender('move', 'post', post, () => (
      <ActionButton data-hook="post-actions__move" onClick={this.showModal(MODAL_TYPE_MOVE_POST)}>
        <MoveIcon className="forum-icon-fill"/>
        {t('post-actions.move-to-cateogory')}
      </ActionButton>
    ));
    const reportButton = canRender('report', 'post', post, can => (
      <ActionButton
        data-hook="post-actions__report"
        onClick={can ? this.showModal(MODAL_TYPE_REPORT_POST) : forPublicUser(this.showModal(MODAL_TYPE_REPORT_POST))}
      >
        <ReportIcon className="forum-icon-fill"/>
        {t('post-actions.report-post')}
      </ActionButton>
    ));
    const deleteButton = canRender('delete', 'post', post, () => (
      <ActionButton data-hook="post-actions__delete" onClick={this.showModal(MODAL_TYPE_DELETE_POST)}>
        <DeleteIcon className="forum-icon-fill"/>
        {t('post-actions.delete-post')}
      </ActionButton>
    ));
    const subscribeButton = canRender('subscribe', 'post', post, () => (
      <ActionButton data-hook="post-actions__subscriptions" onClick={forPublicUser(this.handleSubscription)}>
        <SubscribeIcon isSubscribed={post.isSubscribed} className="forum-icon-fill"/>
        {post.isSubscribed ? t('post-actions.unfollow') : t('post-actions.follow')}
      </ActionButton>
    ));
    return (
      <div>
        {subscribeButton}
        {editButton}
        {shareButton}
        {pinButton}
        {commentsButton}
        {moveButton}
        {reportButton}
        {deleteButton}
      </div>
    );
  }
}

PostActions.propTypes = {
  forPublicUser: PropTypes.func,
  subscribe: PropTypes.func,
  unsubscribe: PropTypes.func,
  t: PropTypes.func,
  buttonClicked: PropTypes.func,
  post: PropTypes.object.isRequired,
  category: PropTypes.object,
  categoryCount: PropTypes.number,
  openModal: PropTypes.func,
  focusOnCloseId: PropTypes.string,
  showMessage: PropTypes.func,
  pinPost: PropTypes.func,
  unpinPost: PropTypes.func,
  enablePostComments: PropTypes.func,
  disablePostComments: PropTypes.func,
  actionsOpened: PropTypes.func,
  actionsClosed: PropTypes.func,
  goToEditPage: PropTypes.func,
  canRender: PropTypes.func,
  navigation: PropTypes.object,
};

const mapStateToProps = (state, { post }) => ({
  category: getCategory(state, post.categoryId),
  categoryCount: getCategoryCount(state),
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({
    openModal,
    subscribe,
    unsubscribe,
    actionsOpened,
    actionsClosed,
    pinPost,
    unpinPost,
    enablePostComments,
    disablePostComments,
    buttonClicked: data => buttonClicked({ name: 'action_click', type: 'post', ...data }),
  }, dispatch);

export default flowRight(
  withEnvironment({
    navigation: 'navigation',
    showMessage: 'showMessage',
  }),
  connect(mapStateToProps, mapDispatchToProps),
  withAuth,
  withPermissions,
  withTranslate
)(PostActions);



// WEBPACK FOOTER //
// ./app/containers/post-actions/post-actions.js